import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import api from '../api/client';
import {
  Activity, ChevronRight, Search, Plus, FileText, Heart, Download,
  Stethoscope, Pill, AlertTriangle, ChevronDown, ChevronUp,
  Thermometer, FlaskConical, Brain, Users, X, Pencil, CheckCircle2, XCircle, Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import Pagination from '../components/Pagination';
import PatientActionResponses from '../components/PatientActionResponses';
import ActionCenter from '../components/ActionCenter';
import SymptomForm from '../components/SymptomForm';
import VitalsCard from '../components/VitalsCard';
import LabResultsCard from '../components/LabResultsCard';
import PatientAccessLog from '../components/PatientAccessLog';

/**
 * MyHealthPage — Unified health page for patients.
 * Combines dashboard stats + symptom list with filters.
 * Replaces both HealthLog and RecordsPage.
 */
const MyHealthPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isDoctor = user?.role === 'DOCTOR';
  const [stats, setStats] = useState(null);
  const [symptoms, setSymptoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDetailedView, setShowDetailedView] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [adherenceLogs, setAdherenceLogs] = useState({}); // { medId: 'taken'|'skipped'|null }
  const [loggingAdherence, setLoggingAdherence] = useState(null); // medId being saved

  // Clinical record state (patient-only — read-only display)
  const [clinicalProfile, setClinicalProfile] = useState(null);
  const [clinicalOpen, setClinicalOpen] = useState(null); // 'conditions'|'medications'|'allergies'|'transitions'

  // Care plan action history (patient-only)
  const [actionHistory, setActionHistory] = useState([]);
  const [actionHistoryLoading, setActionHistoryLoading] = useState(false);
  const [actionHistoryPage, setActionHistoryPage] = useState(1);
  const [actionHistoryTotal, setActionHistoryTotal] = useState(0);

  // Quick report modal (patient-only)
  const [showQuickReport, setShowQuickReport] = useState(false);

  // Vital + social history forms (patient-only)
  const [showAddVital, setShowAddVital] = useState(false);
  const [vitalForm, setVitalForm] = useState({ type: 'blood_pressure', value: '', unit: 'mmHg', notes: '' });
  const [savingVital, setSavingVital] = useState(false);
  const [showEditSocial, setShowEditSocial] = useState(false);
  const [socialForm, setSocialForm] = useState({});
  const [savingSocial, setSavingSocial] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const endpoint = isDoctor
        ? `/symptoms?status=${statusFilter}&page=${page}&limit=20`
        : `/symptoms/me?status=${statusFilter}&page=${page}&limit=20`;
      const [statsRes, symptomsRes] = await Promise.all([
        api.get('/symptoms/stats/dashboard').catch(() => null),
        api.get(endpoint).catch(() => null),
      ]);
      if (statsRes?.data?.success) setStats(statsRes.data.data);
      const data = symptomsRes?.data?.data || symptomsRes?.data || [];
      setSymptoms(Array.isArray(data) ? data : []);
      setTotalPages(symptomsRes?.data?.pagination?.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch health data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClinical = async () => {
    if (isDoctor) return;
    try {
      const res = await api.get('/patient-profile/me');
      if (res?.data?.success) setClinicalProfile(res.data.data);
    } catch { /* non-fatal */ }
  };

  const fetchActionHistory = async () => {
    if (isDoctor) return;
    setActionHistoryLoading(true);
    try {
      const res = await api.get(`/risk-assessments/my-actions?page=${actionHistoryPage}&limit=10`);
      if (res?.data?.success) {
        setActionHistory(res.data.data || []);
        setActionHistoryTotal(res.data.pagination?.total || 0);
      }
    } catch { /* non-fatal */ }
    finally { setActionHistoryLoading(false); }
  };

  const VITAL_UNITS = {
    blood_pressure: 'mmHg', heart_rate: 'bpm', weight: 'kg',
    temperature: '°C', oxygen_saturation: '%', blood_glucose: 'mg/dL', respiratory_rate: 'breaths/min',
  };

  const handleAddVital = async () => {
    if (!vitalForm.value) { toast.error('Value is required'); return; }
    setSavingVital(true);
    try {
      await api.post('/patient-profile/me/vitals', vitalForm);
      toast.success('Vital recorded');
      setShowAddVital(false);
      setVitalForm({ type: 'blood_pressure', value: '', unit: VITAL_UNITS.blood_pressure, notes: '' });
      fetchClinical();
    } catch { toast.error('Failed to save vital'); }
    finally { setSavingVital(false); }
  };

  const handleSaveSocial = async () => {
    setSavingSocial(true);
    try {
      await api.put('/patient-profile/me/social-history', socialForm);
      toast.success('Social history updated');
      setShowEditSocial(false);
      fetchClinical();
    } catch { toast.error('Failed to update social history'); }
    finally { setSavingSocial(false); }
  };

  useEffect(() => { fetchData(); }, [statusFilter, page]);
  useEffect(() => { fetchClinical(); }, []);
  useEffect(() => { fetchActionHistory(); }, [actionHistoryPage]);

  const ACTION_LABELS = {
    urgent_clinical_review:   'Clinical Review Needed',
    schedule_home_visit:      'Home Visit Arranged',
    pharmacy_outreach:        'Medication Support',
    caregiver_notification:   'Caregiver Check-in',
    care_coordinator_outreach:'Care Coordination',
    schedule_virtual_visit:   'Virtual Visit Scheduled',
    medication_review:        'Medication Review',
    self_care_guidance:       'Self-Care Guidance',
    emergency_referral:       'Emergency Referral',
    follow_up_scheduling:     'Follow-up Scheduled',
    notify_pharmacy:          'Pharmacy Notified',
    notify_caregiver:         'Caregiver Notified',
    schedule_appointment:     'Appointment Scheduled',
    send_video_link:          'Video Link Sent',
    send_educational_materials:'Educational Materials Sent',
    schedule_follow_up_check: 'Follow-up Check Scheduled',
    schedule_pharmacy_call:   'Pharmacy Call Scheduled',
    schedule_welfare_check:   'Welfare Check Scheduled',
  };

  const getSevStyle = (sev) => {
    const m = {
      urgent: { bg: 'rgba(186,26,26,0.1)', color: '#ba1a1a', label: 'Urgent' },
      high: { bg: 'rgba(249,115,22,0.1)', color: '#F97316', label: 'High' },
      medium: { bg: 'rgba(95,67,0,0.1)', color: '#5f4300', label: 'Medium' },
      low: { bg: 'rgba(0,105,107,0.1)', color: '#00696b', label: 'Low' },
    };
    return m[sev] || m.low;
  };

  const getStatusStyle = (status) => {
    const m = {
      OPEN: 'bg-amber-100 text-amber-700',
      CLOSED: 'bg-slate-100 text-slate-600',
    };
    return m[status] || 'bg-slate-100 text-slate-600';
  };

  const filtered = symptoms.filter(s =>
    !searchTerm || s.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.userId?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-headline text-2xl font-extrabold text-slate-900 tracking-tight">
            {isDoctor ? 'Patient Health Log' : 'My Health'}
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            {isDoctor ? 'View and manage symptoms across your patients' : 'Your health reports and history'}
          </p>
        </div>
        {!isDoctor && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowQuickReport(true)}
              className="flex items-center gap-2 px-4 py-3 bg-white text-primary border border-primary/20 rounded-2xl font-bold text-sm hover:bg-primary/5 active:scale-95 transition-all"
            >
              <Plus className="w-4 h-4" />
              Quick Report
            </button>
            <button
              disabled={exporting}
              onClick={async () => {
                setExporting(true);
                try {
                  const res = await api.post('/transparency/export-my-data', { format: 'json' });
                  const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `my-health-data-${new Date().toISOString().slice(0,10)}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success('Health data exported');
                } catch {
                  toast.error('Failed to export data');
                } finally {
                  setExporting(false);
                }
              }}
              className="flex items-center gap-2 px-4 py-3 bg-white text-primary border border-primary/20 rounded-2xl font-bold text-sm hover:bg-primary/5 active:scale-95 transition-all disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {exporting ? 'Exporting...' : 'Download My Data'}
            </button>
            <button
              onClick={() => navigate('/report')}
              className="flex items-center gap-2 px-5 py-3 bg-primary text-white rounded-2xl font-bold text-sm shadow-md hover:shadow-lg active:scale-95 transition-all"
            >
              <Plus className="w-4 h-4" />
              Report Issue
            </button>
          </div>
        )}
      </div>

      {/* Health Risk Status (AI-powered overview + recommended actions) */}
      {!isDoctor && <ActionCenter />}

      {/* Pending Actions from Care Team */}
      {!isDoctor && <PatientActionResponses />}

      {/* ── Your Care Plan History ── */}
      {!isDoctor && (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-headline text-lg font-bold text-slate-900">Your Care Plan</h3>
            <span className="text-xs text-slate-500">{actionHistoryTotal} total actions</span>
          </div>
          {actionHistoryLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <div key={i} className="h-16 rounded-2xl animate-pulse bg-slate-100" />)}
            </div>
          ) : actionHistory.length === 0 ? (
            <div className="bg-slate-50 rounded-2xl p-6 text-center">
              <FileText className="w-8 h-8 mx-auto mb-2 text-slate-400" />
              <p className="text-sm text-slate-500">No care plan actions yet.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {actionHistory.map((action) => {
                const statusColor = {
                  completed: 'text-emerald-600 bg-emerald-50',
                  acknowledged: 'text-blue-600 bg-blue-50',
                  dismissed: 'text-slate-500 bg-slate-100',
                  expired: 'text-slate-400 bg-slate-50',
                  pending: 'text-amber-600 bg-amber-50',
                  viewed: 'text-primary bg-primary/10',
                };
                const priorityDot = {
                  critical: 'bg-red-500',
                  high: 'bg-orange-400',
                  medium: 'bg-amber-400',
                  low: 'bg-emerald-400',
                };
                const cls = statusColor[action.status] || statusColor.pending;
                const dot = priorityDot[action.priority] || priorityDot.low;
                return (
                  <div key={action._id} className="flex items-center gap-4 bg-white rounded-xl border border-outline-variant/10 px-4 py-3">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate">
                        {action.patientFacing?.title || ACTION_LABELS[action.actionType] || action.actionType?.replace(/_/g, ' ')}
                      </p>
                      {action.patientFacing?.message && (
                        <p className="text-xs text-slate-500 truncate mt-0.5">{action.patientFacing.message}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold capitalize ${cls}`}>{action.status}</span>
                      <span className="text-xs text-slate-400">
                        {new Date(action.generatedAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  </div>
                );
              })}
              {actionHistoryTotal > actionHistory.length && (
                <div className="flex justify-center gap-3 pt-1">
                  {actionHistoryPage > 1 && (
                    <button onClick={() => setActionHistoryPage(p => p - 1)} className="px-4 py-2 text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50">Previous</button>
                  )}
                  <button onClick={() => setActionHistoryPage(p => p + 1)} className="px-4 py-2 text-xs font-bold text-primary bg-primary/5 border border-primary/20 rounded-xl hover:bg-primary/10">Load more</button>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* ── Patient Clinical Record (read-only — managed by care team) ── */}
      {!isDoctor && clinicalProfile && (() => {
        const conditions = (clinicalProfile.conditions || []).filter(c => c.status === 'active');
        const medications = (clinicalProfile.medications || []).filter(m => m.status === 'active');
        const allergies = clinicalProfile.allergies || [];
        const transitions = clinicalProfile.careTransitions || [];
        const today = new Date().toISOString().slice(0, 10);

        const logAdherence = async (medId, taken) => {
          setLoggingAdherence(medId);
          try {
            await api.post(`/patient-profile/me/medications/${medId}/log`, { taken, date: today });
            setAdherenceLogs(prev => ({ ...prev, [medId]: taken ? 'taken' : 'skipped' }));
            toast.success(taken ? 'Logged as taken' : 'Logged as skipped');
          } catch {
            toast.error('Failed to save');
          } finally {
            setLoggingAdherence(null);
          }
        };

        // Seed adherence logs from profile data for today
        medications.forEach(m => {
          if (adherenceLogs[m._id] === undefined) {
            const todayLog = m.adherence?.dailyLogs?.find(l => l.date === today);
            if (todayLog) {
              setAdherenceLogs(prev => ({ ...prev, [m._id]: todayLog.taken ? 'taken' : 'skipped' }));
            }
          }
        });

        const Section = ({ id, icon: Icon, title, count, children }) => (
          <div className="bg-white rounded-2xl border border-outline-variant/10 shadow-sm overflow-hidden">
            <button
              onClick={() => setClinicalOpen(o => o === id ? null : id)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-primary" />
                </div>
                <span className="font-bold text-sm text-slate-900">{title}</span>
                <span className="text-xs font-bold px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">{count}</span>
              </div>
              {clinicalOpen === id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            {clinicalOpen === id && (
              <div className="px-4 pb-4 space-y-2">{children}</div>
            )}
          </div>
        );

        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm text-slate-500 uppercase tracking-widest">My Clinical Record</h3>
              <span className="text-xs text-slate-400">Managed by your care team</span>
            </div>

            {/* Conditions */}
            <Section id="conditions" icon={Stethoscope} title="Conditions" count={conditions.length}>
              {conditions.length === 0
                ? <p className="text-sm text-slate-400 py-2 text-center">No active conditions on file</p>
                : conditions.map((c) => (
                  <div key={c._id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{c.name}</p>
                      <p className="text-xs text-slate-500">Since {new Date(c.diagnosedDate).toLocaleDateString()}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${c.severity === 'severe' ? 'bg-red-50 text-red-600' : c.severity === 'moderate' ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>{c.severity}</span>
                  </div>
                ))
              }
            </Section>

            {/* Medications */}
            <Section id="medications" icon={Pill} title="Medications" count={medications.length}>
              {medications.length === 0
                ? <p className="text-sm text-slate-400 py-2 text-center">No active medications on file</p>
                : medications.map((m) => {
                  const log = adherenceLogs[m._id];
                  const isLogging = loggingAdherence === m._id;
                  const rate = m.adherence?.rate != null ? Math.round(m.adherence.rate) : null;
                  return (
                    <div key={m._id} className="p-3 bg-slate-50 rounded-xl space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">{m.name}</p>
                          <p className="text-xs text-slate-500">{m.dosage && `${m.dosage} · `}{m.frequency}</p>
                        </div>
                        {rate != null && (
                          <span className={`shrink-0 px-2 py-0.5 rounded text-xs font-bold ${rate >= 80 ? 'bg-emerald-50 text-emerald-600' : rate >= 50 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-500'}`}>
                            {rate}% adherence
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {log === 'taken' ? (
                          <span className="flex items-center gap-1 text-xs font-bold text-emerald-600">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Taken today
                          </span>
                        ) : log === 'skipped' ? (
                          <span className="flex items-center gap-1 text-xs font-bold text-slate-400">
                            <XCircle className="w-3.5 h-3.5" /> Skipped today
                          </span>
                        ) : (
                          <>
                            <button
                              onClick={() => logAdherence(m._id, true)}
                              disabled={isLogging}
                              className="flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-lg hover:bg-emerald-100 transition-colors disabled:opacity-50"
                            >
                              {isLogging ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                              Took it
                            </button>
                            <button
                              onClick={() => logAdherence(m._id, false)}
                              disabled={isLogging}
                              className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-500 text-xs font-bold rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
                            >
                              <XCircle className="w-3 h-3" /> Skip
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              }
            </Section>

            {/* Allergies */}
            <Section id="allergies" icon={AlertTriangle} title="Allergies" count={allergies.length}>
              {allergies.length === 0
                ? <p className="text-sm text-slate-400 py-2 text-center">No allergies on file</p>
                : allergies.map((a) => (
                  <div key={a._id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{a.allergen}</p>
                      {a.reaction && <p className="text-xs text-slate-500">{a.reaction}</p>}
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${a.severity === 'life_threatening' ? 'bg-red-100 text-red-700' : a.severity === 'severe' ? 'bg-red-50 text-red-600' : a.severity === 'moderate' ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>{a.severity?.replace('_', ' ')}</span>
                  </div>
                ))
              }
            </Section>

            {/* Care Transitions */}
            <Section id="transitions" icon={FileText} title="Care History" count={transitions.length}>
              {transitions.length === 0
                ? <p className="text-sm text-slate-400 py-2 text-center">No care history on file</p>
                : (() => {
                  const labels = { discharge: 'Hospital Discharge', admission: 'Hospital Admission', er_visit: 'ER Visit', clinic_visit: 'Clinic Visit', home_health_start: 'Home Health Started', home_health_end: 'Home Health Ended' };
                  return transitions.slice(0, 5).map((t) => (
                    <div key={t._id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{labels[t.type] || t.type}</p>
                        {t.facility && <p className="text-xs text-slate-500">{t.facility}</p>}
                      </div>
                      <p className="text-xs font-bold text-slate-500">{new Date(t.date).toLocaleDateString()}</p>
                    </div>
                  ));
                })()
              }
            </Section>

            {/* Vitals — upgraded component with LOINC codes + normal/abnormal badges */}
            <VitalsCard
              vitals={clinicalProfile.vitals || []}
              canView={true}
              isDoctor={false}
              onAdded={fetchClinical}
            />

            {/* Lab Results — upgraded component with source flag */}
            <LabResultsCard
              labResults={clinicalProfile.labResults || []}
              canView={true}
              isDoctor={false}
            />

            {/* Social History */}
            <Section
              id="socialHistory"
              icon={Users}
              title="Social History"
              count={['smokingStatus', 'alcoholUse', 'exerciseFrequency', 'livingSituation', 'employmentStatus'].filter(k => clinicalProfile.socialHistory?.[k]).length}
            >
              {(() => {
                const sh = clinicalProfile.socialHistory || {};
                const labelMap = {
                  smokingStatus: 'Smoking', alcoholUse: 'Alcohol Use',
                  exerciseFrequency: 'Exercise', livingSituation: 'Living Situation', employmentStatus: 'Employment',
                };
                const hasData = ['smokingStatus', 'alcoholUse', 'exerciseFrequency', 'livingSituation', 'employmentStatus'].some(k => sh[k]);
                return (
                  <>
                    {!showEditSocial && (
                      <button
                        onClick={() => {
                          setSocialForm({ smokingStatus: sh.smokingStatus || '', alcoholUse: sh.alcoholUse || '', exerciseFrequency: sh.exerciseFrequency || '', livingSituation: sh.livingSituation || '', employmentStatus: sh.employmentStatus || '', notes: sh.notes || '' });
                          setShowEditSocial(true);
                        }}
                        className="mb-3 flex items-center gap-1.5 text-xs font-bold text-primary hover:bg-primary/5 px-3 py-1.5 rounded-lg transition-all border border-primary/20"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                    )}
                    {showEditSocial ? (
                      <div className="space-y-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                        {[
                          { key: 'smokingStatus', label: 'Smoking Status', options: ['never', 'former', 'current', 'passive'] },
                          { key: 'alcoholUse', label: 'Alcohol Use', options: ['none', 'occasional', 'moderate', 'heavy'] },
                          { key: 'exerciseFrequency', label: 'Exercise', options: ['none', 'light', 'moderate', 'active', 'very_active'] },
                          { key: 'livingSituation', label: 'Living Situation', options: ['alone', 'with_family', 'with_roommates', 'assisted_living', 'homeless'] },
                          { key: 'employmentStatus', label: 'Employment', options: ['employed', 'unemployed', 'retired', 'student', 'disabled'] },
                        ].map(({ key, label, options }) => (
                          <div key={key}>
                            <label className="text-xs font-bold text-slate-500 block mb-1">{label}</label>
                            <select
                              value={socialForm[key] || ''}
                              onChange={e => setSocialForm(p => ({ ...p, [key]: e.target.value }))}
                              className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg"
                            >
                              <option value="">Select...</option>
                              {options.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
                            </select>
                          </div>
                        ))}
                        <textarea
                          placeholder="Notes (optional)"
                          value={socialForm.notes || ''}
                          onChange={e => setSocialForm(p => ({ ...p, notes: e.target.value }))}
                          rows={2}
                          className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg resize-none"
                        />
                        <div className="flex gap-2">
                          <button onClick={handleSaveSocial} disabled={savingSocial} className="flex-1 py-2 bg-primary text-white rounded-lg text-sm font-bold disabled:opacity-50">
                            {savingSocial ? 'Saving...' : 'Save'}
                          </button>
                          <button onClick={() => setShowEditSocial(false)} className="flex-1 py-2 bg-slate-200 text-slate-600 rounded-lg text-sm font-bold">Cancel</button>
                        </div>
                      </div>
                    ) : hasData ? (
                      <div className="space-y-2">
                        {Object.entries(labelMap).filter(([k]) => sh[k]).map(([k, label]) => (
                          <div key={k} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                            <p className="text-sm font-semibold text-slate-900">{label}</p>
                            <p className="text-sm text-slate-600 capitalize">{sh[k]?.replace(/_/g, ' ')}</p>
                          </div>
                        ))}
                        {sh.notes && <p className="text-xs text-slate-500 italic px-1">{sh.notes}</p>}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 py-2 text-center">No social history recorded — tap Edit to add</p>
                    )}
                  </>
                );
              })()}
            </Section>

            {/* Mental Health */}
            <Section
              id="mentalHealth"
              icon={Brain}
              title="Mental Health"
              count={(clinicalProfile.mentalHealth?.diagnoses || []).length}
            >
              {(() => {
                const mh = clinicalProfile.mentalHealth || {};
                const diagnoses = mh.diagnoses || [];
                const hasMH = diagnoses.length > 0 || mh.phq9Score != null || mh.gad7Score != null;
                return hasMH ? (
                  <div className="space-y-3">
                    {diagnoses.map((d, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{d.name}</p>
                          {d.diagnosedAt && <p className="text-xs text-slate-500">Since {new Date(d.diagnosedAt).toLocaleDateString()}</p>}
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs font-bold capitalize ${d.status === 'active' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500'}`}>
                          {d.status}
                        </span>
                      </div>
                    ))}
                    {(mh.phq9Score != null || mh.gad7Score != null) && (
                      <div className="grid grid-cols-2 gap-2">
                        {mh.phq9Score != null && (
                          <div className="p-3 bg-slate-50 rounded-xl text-center">
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">PHQ-9</p>
                            <p className={`text-xl font-extrabold mt-1 ${mh.phq9Score >= 15 ? 'text-red-600' : mh.phq9Score >= 10 ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {mh.phq9Score}
                            </p>
                          </div>
                        )}
                        {mh.gad7Score != null && (
                          <div className="p-3 bg-slate-50 rounded-xl text-center">
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">GAD-7</p>
                            <p className={`text-xl font-extrabold mt-1 ${mh.gad7Score >= 15 ? 'text-red-600' : mh.gad7Score >= 10 ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {mh.gad7Score}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    {mh.lastScreeningDate && (
                      <p className="text-xs text-slate-500 px-1">Last screening: {new Date(mh.lastScreeningDate).toLocaleDateString()}</p>
                    )}
                    {mh.notes && <p className="text-xs text-slate-500 italic px-1">{mh.notes}</p>}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 py-2 text-center">No mental health information on file</p>
                );
              })()}
            </Section>

            {/* Who Accessed My Records — HIPAA patient right */}
            <PatientAccessLog />

            <p className="text-xs text-slate-400 text-center pt-1">
              To update your clinical record, message your care team or contact your doctor.
            </p>
          </div>
        );
      })()}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total Reports', value: stats.totalSymptoms || 0 },
            { label: 'Active', value: stats.activeSymptoms || 0 },
            { label: 'Resolved', value: stats.resolvedCases || 0 },
            { label: 'Urgent', value: stats.urgentCases || 0 },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-2xl p-4 shadow-sm border border-outline-variant/10 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300">
              <p className="text-2xl font-extrabold tracking-tighter mb-0.5">{s.value}</p>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Summary View — shown first for progressive disclosure */}
      {!isDoctor && !showDetailedView && filtered.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-bold text-sm text-slate-500 uppercase tracking-widest">Recent Reports</h3>
          <div className="space-y-3">
            {filtered.slice(0, 3).map((symptom) => {
              const sev = getSevStyle(symptom.severity);
              return (
                <button
                  key={symptom._id}
                  onClick={() => navigate(`/symptom/${symptom._id}`)}
                  className="w-full bg-white rounded-2xl p-5 border border-outline-variant/10 hover:shadow-md transition-all flex items-center gap-4 text-left"
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: sev.bg }}>
                    <Activity className="w-5 h-5" style={{ color: sev.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-sm text-slate-900 break-words">{symptom.title}</h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {new Date(symptom.createdAt).toLocaleDateString()} · <span style={{ color: sev.color }} className="font-bold">{sev.label}</span>
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
                </button>
              );
            })}
          </div>
          {filtered.length > 3 && (
            <button
              onClick={() => setShowDetailedView(true)}
              className="w-full py-3 bg-slate-50 text-primary rounded-2xl font-bold text-sm hover:bg-slate-100 transition-all"
            >
              See all {stats?.totalSymptoms || totalPages * 20} reports
            </button>
          )}
        </div>
      )}

      {/* Detailed view — filters + full list (always shown for doctors) */}
      {(isDoctor || showDetailedView || filtered.length === 0) && (
      <>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            placeholder={isDoctor ? 'Search by patient or symptom...' : 'Search symptoms...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div className="flex gap-2">
          {['ALL', 'OPEN', 'CLOSED'].map((status) => (
            <button
              key={status}
              onClick={() => { setStatusFilter(status); setPage(1); }}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                statusFilter === status
                  ? 'bg-primary text-white shadow-sm'
                  : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              {status === 'ALL' ? 'All' : status === 'OPEN' ? 'Active' : 'Closed'}
            </button>
          ))}
        </div>
      </div>

      {/* Symptom List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-20 rounded-2xl animate-pulse bg-slate-100" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-outline-variant/10">
          <Activity className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <h3 className="font-bold text-lg text-slate-900 mb-1">No health reports</h3>
          <p className="text-sm text-slate-500 mb-4">
            {searchTerm ? 'Try a different search term.' : !isDoctor ? 'Report a health issue to start tracking your symptoms.' : 'No symptoms found.'}
          </p>
          {!isDoctor && !searchTerm && (
            <button
              onClick={() => navigate('/report')}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl font-bold text-sm"
            >
              <Plus className="w-4 h-4" />
              Report Issue
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((symptom) => {
            const sev = getSevStyle(symptom.severity);
            return (
              <button
                key={symptom._id}
                onClick={() => navigate(`/symptom/${symptom._id}`)}
                className="w-full bg-white rounded-2xl p-5 border border-outline-variant/10 hover:shadow-md transition-all flex items-center gap-4 text-left"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: sev.bg }}>
                  <Activity className="w-5 h-5" style={{ color: sev.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h4 className="font-bold text-sm text-slate-900 break-words">{symptom.title}</h4>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${getStatusStyle(symptom.status)}`}>
                      {symptom.status === 'OPEN' ? 'Active' : 'Closed'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    {isDoctor && symptom.userId?.name && (
                      <span className="font-medium text-slate-600">{symptom.userId.name}</span>
                    )}
                    <span>{new Date(symptom.createdAt).toLocaleDateString()}</span>
                    <span className="font-bold" style={{ color: sev.color }}>{sev.label}</span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </>
      )}

      {/* Quick Report Modal */}
      {showQuickReport && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowQuickReport(false); }}
        >
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 shrink-0">
              <div>
                <h3 className="font-headline font-bold text-lg text-slate-900">Quick Report</h3>
                <p className="text-xs text-slate-500 mt-0.5">Tell us what you're experiencing</p>
              </div>
              <button
                onClick={() => setShowQuickReport(false)}
                className="p-2 rounded-xl hover:bg-slate-100 transition-all"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            {/* Scrollable form body */}
            <div className="overflow-y-auto p-6">
              <SymptomForm
                onSymptomAdded={() => {
                  setShowQuickReport(false);
                  fetchData();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyHealthPage;
